import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  Input,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Helmet } from 'react-helmet-async';
import { useEffect, useRef, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import * as Yup from 'yup';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { toast } from '../components/toast';
import { getClients, uploadClaimChargeSubmissionFile } from '../services/api';

const validationSchema = Yup.object().shape({
  selectedClient: Yup.string().required('Please select a client'),
  file: Yup.mixed().required('Please select a file'),
});

const formatFileSize = (bytes) => {
  if (!bytes) {
    return '0 KB';
  }

  const units = ['B', 'KB', 'MB', 'GB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const size = bytes / 1024 ** exponent;

  return `${size.toFixed(size >= 10 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
};

export default function ClaimChargeSubmissionUploadPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientsMessage, setClientsMessage] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({ loaded: 0, total: 0, percent: 0 });
  const [uploadStage, setUploadStage] = useState('idle');
  const [fileError, setFileError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await getClients(1, 1000, '');
        const allClients = response?.data?.results || [];

        if (allClients.length > 0) {
          setClients(allClients);
          setClientsMessage('');
        } else {
          setClientsMessage('No clients available.');
        }
      } catch (error) {
        toast.error('Failed to load clients. Please try again.');
      }
    };

    fetchClients();
  }, []);

  const handleClientChange = (event) => {
    setSelectedClient(event.target.value);
    if (validationErrors.selectedClient) {
      setValidationErrors((prev) => ({ ...prev, selectedClient: '' }));
    }
  };

  const handleFileChange = (event) => {
    const uploadedFile = event.target.files[0];

    const validFileType =
      uploadedFile &&
      (uploadedFile.name.endsWith('.xlsx') || uploadedFile.name.endsWith('.xls') || uploadedFile.name.endsWith('.csv'));

    if (!validFileType) {
      setFileError('Invalid file type. Only excel and csv files are allowed.');
      setFile(null);
      return;
    }

    if (uploadedFile.size > 30 * 1024 * 1024) {
      setFileError('File size exceeds 30 MB');
      setFile(null);
      return;
    }

    setFile(uploadedFile);
    setFileError('');
    if (validationErrors.file) {
      setValidationErrors((prev) => ({ ...prev, file: '' }));
    }
  };

  const validateForm = async () => {
    try {
      await validationSchema.validate(
        {
          selectedClient,
          file,
        },
        { abortEarly: false }
      );
      setValidationErrors({});
      return true;
    } catch (error) {
      const errors = {};
      error.inner.forEach((err) => {
        errors[err.path] = err.message;
      });
      setValidationErrors(errors);
      return false;
    }
  };

  const handleUpload = async () => {
    setOpenConfirmDialog(false);
    const isValid = await validateForm();
    if (!isValid) {
      toast.error('Please fix the form errors and try again.');
      return;
    }

    setIsLoading(true);
    setUploadStage('uploading');
    setUploadProgress({ loaded: 0, total: file?.size || 0, percent: 0 });
    try {
      const response = await uploadClaimChargeSubmissionFile(file, selectedClient, (progressEvent) => {
        const loaded = progressEvent?.loaded || 0;
        const total = progressEvent?.total || file?.size || 0;
        const percent = total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
        setUploadProgress({ loaded, total, percent });

        if (total > 0 && loaded >= total) {
          setUploadStage('processing');
        }
      });
      const backgroundTaskId = response.data?.background_task_id;
      if (backgroundTaskId) {
        toast.success(
          `Background task has been created with task ID: ${backgroundTaskId}. Check its status on the Tasks page.`
        );
      } else {
        const message = response.data?.message || 'File uploaded successfully.';
        toast.success(message);
      }

      // Keep progress visible briefly at 100% so completion is clear to users.
      const totalBytes = uploadProgress.total || file?.size || 0;
      setUploadProgress({ loaded: totalBytes, total: totalBytes, percent: 100 });
      setUploadStage('processing');
      await new Promise((resolve) => setTimeout(resolve, 700));
      resetForm();
    } catch (error) {
      const message = error?.response?.data?.message || 'Unable to process claim charge submission file. Try again later.';
      toast.error(message);
      setUploadProgress({ loaded: 0, total: 0, percent: 0 });
      setUploadStage('idle');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearFile = () => {
    setFile(null);
    setUploadProgress({ loaded: 0, total: 0, percent: 0 });
    setUploadStage('idle');
    setFileError('');
    setValidationErrors((prev) => ({ ...prev, file: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const resetForm = () => {
    setFile(null);
    setUploadProgress({ loaded: 0, total: 0, percent: 0 });
    setUploadStage('idle');
    setFileError('');
    setSelectedClient('');
    setValidationErrors({});
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const hasClients = clients.length > 0;
  const selectedClientName = clients.find((client) => client.id.toString() === selectedClient.toString())?.practice_name || '';
  const isSubmitDisabled = isLoading || !hasClients || !selectedClient || !file || !!fileError;
  const uploadedMB = (uploadProgress.loaded / (1024 * 1024)).toFixed(2);
  const totalMB = ((uploadProgress.total || 0) / (1024 * 1024)).toFixed(2);

  const handleSubmitClick = async () => {
    const isValid = await validateForm();
    if (!isValid) {
      toast.error('Please fix the form errors and try again.');
      return;
    }

    setOpenConfirmDialog(true);
  };

  return (
    <>
      <Helmet>
        <title>BillerBay Transmit | Claim Charge Submission Upload</title>
      </Helmet>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Stack spacing={1}>
            <Typography variant="h4">Claim Charge Submission Upload</Typography>
            <Typography variant="body1" color="text.secondary">
              Upload claim charge submission files from worksheet Detail. Ensure the file has required columns before submitting.
            </Typography>
          </Stack>
        </Grid>

        {!hasClients && clientsMessage && (
          <Grid item xs={12}>
            <Card
              variant="outlined"
              sx={{
                borderColor: 'warning.light',
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12),
              }}
            >
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <InfoOutlinedIcon color="warning" />
                  <Typography variant="body1" color="text.secondary">
                    {clientsMessage}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12} md={8} lg={6}>
          <Card elevation={3} sx={{ height: '100%' }}>
            <CardHeader title="Upload details" subheader="Attach claim charges file and submit for processing." />
            <Divider />
            <CardContent>
              <Stack spacing={3}>
                <FormControl fullWidth error={!!validationErrors.selectedClient} disabled={!hasClients}>
                  <InputLabel id="client-select-label">Select Client</InputLabel>
                  <Select
                    labelId="client-select-label"
                    id="client-select"
                    value={selectedClient}
                    label="Select Client"
                    onChange={handleClientChange}
                    disabled={!hasClients}
                  >
                    {clients.map((client) => (
                      <MenuItem key={client.id} value={client.id}>
                        {client.practice_name}
                      </MenuItem>
                    ))}
                  </Select>
                  {validationErrors.selectedClient && <FormHelperText>{validationErrors.selectedClient}</FormHelperText>}
                </FormControl>

                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="flex-start"
                  sx={(theme) => ({
                    p: 2,
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.info.main, 0.08),
                    border: `1px dashed ${theme.palette.info.light}`,
                  })}
                >
                  <InfoOutlinedIcon color="info" />
                  <Typography variant="body2" color="text.secondary">
                    Expected worksheet: <strong>Detail</strong>. Supported files: .xlsx, .xls, .csv.
                  </Typography>
                </Stack>

                <Stack spacing={1}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Upload Claim Charge Submission File
                  </Typography>
                  <FormControl fullWidth error={!!validationErrors.file || !!fileError} disabled={!hasClients}>
                    <Input
                      id="file-input"
                      type="file"
                      onChange={handleFileChange}
                      inputProps={{ accept: '.xlsx, .xls, .csv' }}
                      inputRef={fileInputRef}
                      disabled={!hasClients}
                    />
                    {fileError && <FormHelperText>{fileError}</FormHelperText>}
                    {validationErrors.file && !fileError && <FormHelperText>{validationErrors.file}</FormHelperText>}
                  </FormControl>
                  {file && (
                    <Box
                      sx={(theme) => ({
                        mt: 2,
                        p: 2,
                        borderRadius: 1,
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        border: `1px dashed ${theme.palette.primary.light}`,
                      })}
                    >
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                        <Stack direction="row" spacing={1.5} alignItems="center" flexGrow={1}>
                          <UploadFileIcon color="primary" />
                          <Box>
                            <Typography variant="subtitle2">{file.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatFileSize(file.size)}
                            </Typography>
                          </Box>
                        </Stack>
                        <Tooltip title="Remove file">
                          <Button
                            variant="text"
                            color="inherit"
                            startIcon={<HighlightOffIcon />}
                            onClick={handleClearFile}
                            sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
                          >
                            Remove
                          </Button>
                        </Tooltip>
                      </Stack>
                      {isLoading && uploadProgress.total > 0 && (
                        <Stack spacing={0.75} mt={2}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" color="text.secondary">
                              {uploadStage === 'processing'
                                ? `Upload complete (${totalMB} MB). Processing on server...`
                                : `Uploading: ${uploadedMB} MB / ${totalMB} MB`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {uploadStage === 'processing' ? '100%' : `${uploadProgress.percent}%`}
                            </Typography>
                          </Stack>
                          <LinearProgress
                            variant={uploadStage === 'processing' ? 'indeterminate' : 'determinate'}
                            value={uploadProgress.percent}
                          />
                        </Stack>
                      )}
                    </Box>
                  )}
                </Stack>
              </Stack>
            </CardContent>
            <Divider />
            <CardActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Supports `.xlsx`, `.xls`, or `.csv` files up to 30 MB.
              </Typography>
              <LoadingButton
                variant="contained"
                sx={{ boxShadow: 'none', backgroundColor: '#0A9EF2', '&:hover': { backgroundColor: '#088ED1' } }}
                disabled={isSubmitDisabled}
                onClick={handleSubmitClick}
                loading={isLoading}
                startIcon={!isLoading ? <UploadFileIcon /> : undefined}
              >
                Submit file
              </LoadingButton>
            </CardActions>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
        <DialogTitle>Confirm Submission</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Confirm submission for client <strong>{selectedClientName || 'Selected Client'}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)} disabled={isLoading}>
            Cancel
          </Button>
          <LoadingButton onClick={handleUpload} loading={isLoading} variant="contained">
            Confirm & Submit
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
